import React, { useState } from 'react';
import {
  Building,
  Star,
  Phone,
  Mail,
  MapPin,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  Package,
  Clock,
  CheckCircle,
  AlertCircle,
  Search,
  Filter,
  Plus,
  Edit,
  Eye,
  ArrowLeft,
  BarChart3,
  Award,
  FileText,
  Globe,
  CreditCard
} from 'lucide-react';
import { suppliers, supplierEvaluations, supplierPerformanceDashboard } from '../../data/supplierData';

const SupplierManagement = ({ onBack, onSelectSupplier, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Filter suppliers based on search and filters
  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesSearch = supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         supplier.englishName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         supplier.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || supplier.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || supplier.category === categoryFilter;
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'suspended': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return '活躍';
      case 'pending': return '待審核';
      case 'suspended': return '暫停';
      default: return '未知';
    }
  };

  const getRatingStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star 
        key={i} 
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
      />
    ));
  };

  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setActiveView('detail');
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">供應商管理</h1>
          <p className="text-gray-600 mt-1">管理供應商資料、評估和績效</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setActiveView('create')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增供應商</span>
          </button>
          <button
            onClick={() => setActiveView('list')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Eye className="w-4 h-4" />
            <span>查看所有供應商</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">總供應商數</p>
              <p className="text-2xl font-bold text-gray-900">{supplierPerformanceDashboard.summary.totalSuppliers}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <Building className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-sm text-green-600">+2 新增本月</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">活躍供應商</p>
              <p className="text-2xl font-bold text-gray-900">{supplierPerformanceDashboard.summary.activeSuppliers}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <span className="text-sm text-gray-600">
              {Math.round((supplierPerformanceDashboard.summary.activeSuppliers / supplierPerformanceDashboard.summary.totalSuppliers) * 100)}% 活躍率
            </span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">平均評分</p>
              <p className="text-2xl font-bold text-gray-900">{supplierPerformanceDashboard.summary.averageRating}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <Star className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-sm text-green-600">+0.2 比上季</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">準時交貨率</p>
              <p className="text-2xl font-bold text-gray-900">{supplierPerformanceDashboard.summary.onTimeDeliveryRate}%</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-sm text-green-600">+1.5% 比上月</span>
          </div>
        </div>
      </div>

      {/* Top Suppliers */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">優秀供應商</h2>
          <button
            onClick={() => setActiveView('list')}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            查看全部
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {supplierPerformanceDashboard.topSuppliers.map((supplier, index) => {
            const supplierDetail = suppliers.find(s => s.id === supplier.supplierId);
            return (
              <div key={supplier.supplierId} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      index === 0 ? 'bg-yellow-100' : index === 1 ? 'bg-gray-100' : 'bg-orange-100'
                    }`}>
                      <Award className={`w-4 h-4 ${
                        index === 0 ? 'text-yellow-600' : index === 1 ? 'text-gray-600' : 'text-orange-600'
                      }`} />
                    </div>
                    <span className="text-sm font-medium text-gray-600">#{index + 1}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    {getRatingStars(Math.floor(supplier.rating))}
                    <span className="text-sm text-gray-600 ml-1">{supplier.rating}</span>
                  </div>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{supplier.name}</h3>
                <div className="space-y-1 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>採購金額:</span>
                    <span className="font-medium">HK${(supplier.spend / 1000000).toFixed(1)}M</span>
                  </div>
                  <div className="flex justify-between">
                    <span>類別:</span>
                    <span className="font-medium">{supplierDetail?.category || 'N/A'}</span>
                  </div>
                </div>
                <button
                  onClick={() => handleSupplierSelect(supplierDetail)}
                  className="w-full mt-3 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  查看詳情
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Category Spending */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">類別採購分析</h2>
        <div className="space-y-4">
          {supplierPerformanceDashboard.categorySpend.map((category) => (
            <div key={category.category} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-blue-500 rounded"></div>
                <span className="font-medium text-gray-900 capitalize">{category.category}</span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full"
                    style={{ width: `${category.percentage}%` }}
                  ></div>
                </div>
                <span className="text-sm text-gray-600 w-12">{category.percentage}%</span>
                <span className="text-sm font-medium text-gray-900 w-20">
                  HK${(category.spend / 1000000).toFixed(1)}M
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSupplierList = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">供應商列表</h1>
          <p className="text-gray-600 mt-1">管理和查看所有供應商</p>
        </div>
        <button
          onClick={() => setActiveView('dashboard')}
          className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>返回總覽</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">搜尋</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜尋供應商名稱或代碼"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">狀態</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">全部狀態</option>
              <option value="active">活躍</option>
              <option value="pending">待審核</option>
              <option value="suspended">暫停</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">類別</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">全部類別</option>
              <option value="electronics">電子產品</option>
              <option value="machinery">機械設備</option>
              <option value="logistics">物流服務</option>
              <option value="materials">原材料</option>
              <option value="packaging">包裝材料</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setCategoryFilter('all');
              }}
              className="w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
            >
              重置篩選
            </button>
          </div>
        </div>
      </div>

      {/* Supplier Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  供應商
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  類別
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  評分
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  狀態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  合作期間
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  總採購額
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSuppliers.map((supplier) => (
                <tr key={supplier.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <Building className="w-5 h-5 text-gray-500" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{supplier.name}</div>
                        <div className="text-sm text-gray-500">{supplier.code}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                    {supplier.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-1">
                      {getRatingStars(Math.floor(supplier.rating))}
                      <span className="text-sm text-gray-600 ml-1">{supplier.rating}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(supplier.status)}`}>
                      {getStatusText(supplier.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {supplier.history.relationshipDuration}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    HK${(supplier.history.totalValue / 1000000).toFixed(1)}M
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleSupplierSelect(supplier)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      查看
                    </button>
                    <button className="text-gray-600 hover:text-gray-900">
                      編輯
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderSupplierDetail = () => {
    if (!selectedSupplier) return null;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{selectedSupplier.name}</h1>
            <p className="text-gray-600 mt-1">{selectedSupplier.englishName}</p>
          </div>
          <button
            onClick={() => setActiveView('list')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回列表</span>
          </button>
        </div>

        {/* Supplier Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Basic Info */}
          <div className="lg:col-span-2 bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">基本資訊</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">供應商代碼</label>
                <p className="text-gray-900">{selectedSupplier.code}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">類別</label>
                <p className="text-gray-900 capitalize">{selectedSupplier.category}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">成立年份</label>
                <p className="text-gray-900">{selectedSupplier.establishedYear}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">員工數量</label>
                <p className="text-gray-900">{selectedSupplier.financial.employees}人</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">網站</label>
                <a href={selectedSupplier.website} className="text-blue-600 hover:text-blue-800">
                  {selectedSupplier.website}
                </a>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">年營收</label>
                <p className="text-gray-900">HK${(selectedSupplier.financial.annualRevenue / 1000000).toFixed(1)}M</p>
              </div>
            </div>
          </div>

          {/* Performance Summary */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">績效概覽</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">整體評分</span>
                <div className="flex items-center space-x-1">
                  {getRatingStars(Math.floor(selectedSupplier.performance.overallScore))}
                  <span className="text-sm font-medium">{selectedSupplier.performance.overallScore}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">準時交貨率</span>
                <span className="text-sm font-medium">{selectedSupplier.performance.onTimeDeliveryRate}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">品質通過率</span>
                <span className="text-sm font-medium">{selectedSupplier.performance.qualityPassRate}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">響應時間</span>
                <span className="text-sm font-medium">{selectedSupplier.performance.responseTime}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">聯絡資訊</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.entries(selectedSupplier.contacts).map(([type, contact]) => (
              <div key={type} className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2 capitalize">{type}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span>{contact.name} ({contact.englishName})</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{contact.phone}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{contact.email}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Financial Information */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">財務資訊</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">信用額度</label>
              <p className="text-lg font-semibold text-gray-900">HK${selectedSupplier.financial.creditLimit.toLocaleString()}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">付款條件</label>
              <p className="text-gray-900">{selectedSupplier.financial.paymentTerms}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">付款方式</label>
              <p className="text-gray-900">{selectedSupplier.financial.paymentMethod}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">銀行</label>
              <p className="text-gray-900">{selectedSupplier.financial.bankInfo.bankName}</p>
            </div>
          </div>
        </div>

        {/* Capabilities */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">供應能力</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-gray-900 mb-2">產品類別</h3>
              <div className="flex flex-wrap gap-2">
                {selectedSupplier.capabilities.products.map((product, index) => (
                  <span key={index} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                    {product}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 mb-2">服務項目</h3>
              <div className="flex flex-wrap gap-2">
                {selectedSupplier.capabilities.services.map((service, index) => (
                  <span key={index} className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                    {service}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 mb-2">認證證書</h3>
              <div className="flex flex-wrap gap-2">
                {selectedSupplier.capabilities.certifications.map((cert, index) => (
                  <span key={index} className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
                    {cert}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 mb-2">其他資訊</h3>
              <div className="space-y-1 text-sm text-gray-600">
                <p>產能: {selectedSupplier.capabilities.productionCapacity}</p>
                <p>交期: {selectedSupplier.capabilities.leadTime}</p>
                <p>最小訂量: {selectedSupplier.capabilities.moq}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'list':
        return renderSupplierList();
      case 'detail':
        return renderSupplierDetail();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="flex items-center space-x-4">
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>返回採購管理</span>
        </button>
      </div>

      {renderContent()}
    </div>
  );
};

export default SupplierManagement; 